/**
 * OfficeWorkflows (DEV-732) — the office-level workflow customization surface,
 * shown in Office Settings next to Document Templates.
 *
 * An office designer can: customize a system template (deep-clone it into an
 * office-owned copy), edit the graph in the designer, publish it, or build one
 * from scratch. Publishing an office copy makes every program created in the
 * office mint from it automatically (office-first resolution) — no per-user
 * setup. Document customizations flow in separately via the Documents tab; a
 * workflow step only names a document *family*, so an office's customized doc
 * is picked up without touching the workflow.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Copy, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button, Card, Input } from '../../components/ui'
import { useUser } from '../../contexts/UserContext'
import { workflowsApi, type AuthoringWorkflow } from '../../services/workflows'

const BASE_PATH = '/settings/office/workflows'

export default function OfficeWorkflows() {
  const navigate = useNavigate()
  const { user } = useUser()
  const officeId = user?.office?.id ?? null

  const [workflows, setWorkflows] = useState<AuthoringWorkflow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [newKey, setNewKey] = useState('')
  const [newName, setNewName] = useState('')

  const load = useCallback(async () => {
    try {
      setWorkflows(await workflowsApi.listAuthoringWorkflows())
    } catch (e: any) {
      setError(e?.message || 'Failed to load workflows')
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  const officeWorkflows = useMemo(
    () => (workflows ?? []).filter((w) => w.owner_level === 'office' && w.office === officeId),
    [workflows, officeId],
  )
  const officeKeys = useMemo(() => new Set(officeWorkflows.map((w) => w.key)), [officeWorkflows])
  const systemTemplates = useMemo(
    () => (workflows ?? []).filter((w) => w.owner_level === 'system'),
    [workflows],
  )

  const customize = async (tmpl: AuthoringWorkflow) => {
    setBusyKey(tmpl.key); setError(null)
    try {
      const clone = await workflowsApi.cloneWorkflowToOffice(tmpl.id)
      navigate(`${BASE_PATH}/${clone.id}`)
    } catch (e: any) {
      setError(e?.message || 'Could not customize template')
      setBusyKey(null)
    }
  }

  const openOfficeCopy = (key: string) => {
    const wf = officeWorkflows.find((w) => w.key === key)
    if (wf) navigate(`${BASE_PATH}/${wf.id}`)
  }

  const togglePublish = async (wf: AuthoringWorkflow) => {
    try {
      await workflowsApi.updateWorkflow(wf.id, { is_published: !wf.is_published })
      await load()
    } catch (e: any) { setError(e?.message || 'Could not update') }
  }

  const remove = async (wf: AuthoringWorkflow) => {
    if (!confirm(`Delete “${wf.name}”? Programs already created keep their steps.`)) return
    try { await api_delete(wf.id); await load() } catch (e: any) { setError(e?.message || 'Could not delete') }
  }

  const createBlank = async () => {
    if (!newKey.trim() || !newName.trim()) return
    setBusyKey('__new__'); setError(null)
    try {
      const wf = await workflowsApi.createWorkflow({ key: newKey.trim(), name: newName.trim() })
      navigate(`${BASE_PATH}/${wf.id}`)
    } catch (e: any) {
      setError(e?.message || 'Could not create workflow')
      setBusyKey(null)
    }
  }

  if (!officeId) {
    return <p className="text-sm text-muted-foreground">You are not assigned to an office.</p>
  }

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-muted/30">
        <p className="text-sm text-foreground font-medium mb-1">What are workflows?</p>
        <p className="text-xs text-muted-foreground">
          A workflow is the ordered set of <em>modules</em> (steps) a program moves through — capture
          requirements, build scorecards, research vendors, generate documents. Overmatch ships system
          templates; here you tailor them for your office. <strong>Customize a template</strong> to get
          an editable copy, arrange its modules and instructions, then <strong>Publish</strong>. From
          then on, every new program in your office follows your version automatically. (To change the
          <em> contents</em> of a document — e.g. the Market Research report — use the Documents tab; the
          workflow picks that up on its own.)
        </p>
      </Card>

      {error && <div className="text-sm text-destructive">{error}</div>}

      {/* Office's own workflows */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-2">Your office's workflows</h3>
        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
        ) : officeWorkflows.length === 0 ? (
          <p className="text-sm text-muted-foreground">None yet — customize a template below or start from scratch.</p>
        ) : (
          <ul className="space-y-2">
            {officeWorkflows.map((wf) => (
              <li key={wf.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground truncate">{wf.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {wf.key} · {wf.steps.length} steps · {wf.is_published
                      ? <span className="text-green-600">published</span>
                      : <span className="text-amber-600">draft</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`${BASE_PATH}/${wf.id}`)}>
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
                <Button size="sm" variant={wf.is_published ? 'ghost' : 'default'} onClick={() => togglePublish(wf)}>
                  {wf.is_published ? 'Unpublish' : 'Publish'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(wf)} aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* System templates to customize */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-2">Start from a template</h3>
        <ul className="space-y-2">
          {systemTemplates.map((tmpl) => {
            const hasCopy = officeKeys.has(tmpl.key)
            return (
              <li key={tmpl.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground truncate">{tmpl.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{tmpl.description || tmpl.key}</div>
                </div>
                {hasCopy ? (
                  <Button size="sm" variant="outline" onClick={() => openOfficeCopy(tmpl.key)}>
                    <CheckCircle2 className="w-4 h-4 mr-1.5 text-green-600" /> Open your copy
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => customize(tmpl)} disabled={busyKey === tmpl.key}>
                    {busyKey === tmpl.key ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                    Customize
                  </Button>
                )}
              </li>
            )
          })}
          {systemTemplates.length === 0 && (
            <li className="text-sm text-muted-foreground">No system templates available.</li>
          )}
        </ul>
      </section>

      {/* Blank */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-2">Or build from scratch</h3>
        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Key (slug)</span>
              <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="rapid_acquisition" />
            </label>
            <label className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <span className="text-xs text-muted-foreground">Name</span>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Rapid Acquisition" />
            </label>
            <Button onClick={createBlank} disabled={busyKey === '__new__' || !newKey.trim() || !newName.trim()}>
              {busyKey === '__new__' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </div>
        </Card>
      </section>
    </div>
  )
}

// Local helper: the workflows service has no delete for authoring workflows yet.
async function api_delete(id: number) {
  const { api } = await import('../../services/api')
  await api.delete(`/api/workflows/authoring/workflows/${id}/`)
}
